import { EVENT_DELAY } from '@src/config/constants'
import { TRADE_APPLICATION_SUBMISSION } from '@src/quince-components/src/molecules/tradeApplicationForm/constants'
import { initializeImg } from '@src/telemetry/integrations/provider-utils/initialize-img'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { isB2BLandingPageRoute } from '@src/telemetry/utils/track-b2b-form-conversion'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

/**
 * EBB-439: pages whose HubSpot lead-form submit fires the LinkedIn *corp-gifting* lead conversion
 * (`15887476`, set up under EBB-408). Matching is exact after locale/slash canonicalisation, so the
 * `/corporate-gifting-old` rollback copy does not match.
 *
 * Only Corporate Gifting is listed on purpose: hospitality, interior-design and white-label are
 * waiting on marketing for their own conversion ids, and firing the corp-gifting id there would
 * mis-attribute their leads. Add a route here once its id exists.
 */
// eslint-disable-next-line i18next/no-literal-string -- route path, not user-facing copy
const LINKEDIN_GIFTING_CONVERSION_ROUTES = ['/corporate-gifting']

/** LinkedIn's id for the corp-gifting lead conversion. Set up under EBB-408. */
const CORP_GIFTING_LEAD_CONVERSION_ID = 15887476

export const LinkedInPixel: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.LINKEDIN_PIXEL,
    // eslint-disable-next-line i18next/no-literal-string
    featureGateName: 'ssr_telemetry_linked_in-pixel',
  },

  initialize: async () => {
    window._linkedin_partner_id = '1873316'
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? []
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id)

    if (window.lintrk) return
    window.lintrk = function (a, b) {
      if (window.lintrk) (window.lintrk.q = window.lintrk.q ?? []).push([a, b])
    }
    window.lintrk.q = []

    await initializeScript({
      key: 'linkedin-insight',
      domain: TELEMETRY_DOMAINS.LINKEDIN_PIXEL,
      path: '/li.lms-analytics/insight.min.js',
      injectAt: 'beforeFirstScript',
    })

    initializeImg({
      id: 'linkedin_pixel_img',
      src: `https://px.ads.linkedin.com/collect/?pid=${window._linkedin_partner_id}&fmt=gif`,
      // eslint-disable-next-line i18next/no-literal-string
      attributes: { style: 'display:none;' },
    })
  },

  events: {
    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      // Fires for every FORM_SUBMIT on the site, D2C included. Two rules keep that safe: gate on
      // the action, which only the trade/B2B forms set, and never read `context` — producers such
      // as `send_invite` omit it entirely. See EBB-439.
      const isTradeApplicationSubmit = properties.action === TRADE_APPLICATION_SUBMISSION
      if (isTradeApplicationSubmit && isLinkedInGiftingConversionRoute(window.location.pathname)) {
        trackLinkedInGiftingConversion()
      }
    },
  },
}

export const isLinkedInGiftingConversionRoute = (pathname: string | undefined | null): boolean =>
  isB2BLandingPageRoute(pathname, LINKEDIN_GIFTING_CONVERSION_ROUTES)

/** Tells LinkedIn a corp-gifting lead converted, so it can credit the ad that drove it. */
export const trackLinkedInGiftingConversion = () => {
  executeFireAndForgotTask(() => {
    // eslint-disable-next-line i18next/no-literal-string
    if (window.lintrk) window.lintrk('track', { conversion_id: CORP_GIFTING_LEAD_CONVERSION_ID })
  }, EVENT_DELAY)
}
